// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Chat / AI content-generation client for the Бриф pipeline (the "Hermes" flow).
// Wraps SPEC §1 endpoints with EXACT payloads. Every call here is a chat/write
// action, so it goes through the STRICT publisherCall (bearer + JWT pre-check;
// 401 → global logout). Worker rewrites `/api/publisher` → `/api/promotion`, so
// the verbatim `/api/publisher/*` paths below are correct.
//
// AI-degradation contract (SPEC §1a/1b, Gotcha 3): chat-plan and suggestions
// NEVER 4xx for AI failures — they return HTTP 200 with a rule-based / fallback
// payload (`fallback:true` / `selection_provider:'rules'` / `suggestions:[]`).
// publisherCall only throws on a real non-2xx (e.g. the 400 `prompt is required`
// / `body required` guards), so we return the parsed object as-is.
//
// generate-image is intentionally absent — SPEC §1i: HTTP 410, hard-disabled.

import { publisherCall } from './client';
import type {
  ChatChannelMethod,
  ChatIntent,
  MarketingSkill,
  Platform,
  PlatformDraft,
  PromotionChatPlanApiResponse,
  PromotionPost,
  PromotionSuggestionsApiResponse,
  PromotionTranslation,
  SourceContent,
  SourceType,
  SourceMode,
  Strategy,
} from './types';

// ── Shared request fragments ─────────────────────────────────────

/** A channel the user picked in the composer (direct or Buffer). */
export interface SelectedChannel {
  id: string;
  platform: Platform;
  method: ChatChannelMethod;
  label: string;
  buffer_channel_id?: string;
}

function jsonInit(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

// ── 1a. chat-plan ────────────────────────────────────────────────

export interface ChatPlanRequest {
  prompt: string; // required, ≤700; empty → 400 `prompt is required`
  ready_platforms: Platform[];
  selected_channels: SelectedChannel[];
}

/**
 * POST /api/publisher/chat-plan — derive a campaign plan from the user prompt.
 * Happy path is always 200 and degrades to a rule-based plan (`fallback:true`)
 * on AI failure, so we return the parsed response untouched. The only throw is
 * the 400 `prompt is required` guard surfaced by publisherCall.
 */
export function chatPlan(req: ChatPlanRequest): Promise<PromotionChatPlanApiResponse> {
  return publisherCall<PromotionChatPlanApiResponse>(
    '/api/publisher/chat-plan',
    jsonInit({
      prompt: req.prompt,
      ready_platforms: req.ready_platforms,
      selected_channels: req.selected_channels,
    }),
  );
}

// ── 1b. suggestions (candidate selection — core) ─────────────────

export interface SuggestionsRequest {
  language: string;
  source_type: SourceType;
  source_mode: SourceMode;
  strategy: Strategy;
  marketing_skill: MarketingSkill;
  topic: string;
  prompt: string;
  codex_select?: boolean; // default true
  require_cover?: boolean; // default false
  require_deep_link?: boolean; // default true
  intent?: ChatIntent;
  selected_channels?: SelectedChannel[];
  count: number; // clamp 1..5
}

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return Math.min(hi, Math.max(lo, Math.round(value)));
}

/**
 * POST /api/publisher/suggestions — the core candidate-selection step.
 * Always 200; the empty case returns 200 with `suggestions:[]` + `error`, and AI
 * failures degrade to `selection_provider:'rules'`. Either way we hand back the
 * parsed object as-is — callers branch on `suggestions`/`error`.
 */
export function suggestions(req: SuggestionsRequest): Promise<PromotionSuggestionsApiResponse> {
  return publisherCall<PromotionSuggestionsApiResponse>(
    '/api/publisher/suggestions',
    jsonInit({
      language: req.language,
      source_type: req.source_type,
      source_mode: req.source_mode,
      strategy: req.strategy,
      marketing_skill: req.marketing_skill,
      topic: req.topic,
      prompt: req.prompt,
      codex_select: req.codex_select ?? true,
      require_cover: req.require_cover ?? false,
      require_deep_link: req.require_deep_link ?? true,
      ...(req.intent ? { intent: req.intent } : {}),
      ...(req.selected_channels ? { selected_channels: req.selected_channels } : {}),
      count: clamp(req.count, 1, 5),
    }),
  );
}

// ── 1e. variants (A/B per platform) ──────────────────────────────

export interface VariantsRequest {
  source: SourceContent;
  platform: Platform;
  count: number;
}

export interface VariantsResponse {
  variants?: PlatformDraft[];
  error?: string;
}

/**
 * POST /api/publisher/variants — generate A/B body variants for one platform.
 * 400 on `source.title and source.body required` / `valid platform required`.
 */
export function variants(req: VariantsRequest): Promise<VariantsResponse> {
  return publisherCall<VariantsResponse>(
    '/api/publisher/variants',
    jsonInit({ source: req.source, platform: req.platform, count: req.count }),
  );
}

// ── 1f. hashtags ─────────────────────────────────────────────────

export interface HashtagsRequest {
  body: string;
  platform: Platform;
}

export interface HashtagsResponse {
  hashtags?: string[];
  error?: string;
}

/**
 * POST /api/publisher/hashtags — platform-tuned hashtag set (telegram 3,
 * twitter 2, instagram 10, default 5, reddit 0). 400 on `body required` /
 * `valid platform required`.
 */
export function hashtags(req: HashtagsRequest): Promise<HashtagsResponse> {
  return publisherCall<HashtagsResponse>(
    '/api/publisher/hashtags',
    jsonInit({ body: req.body, platform: req.platform }),
  );
}

// ── 1g. translate ────────────────────────────────────────────────

export interface TranslateRequest {
  source: SourceContent;
  direction: 'ru_to_en' | 'en_to_ru';
  count: number; // 1..6
}

export interface TranslateResponse {
  translations?: PromotionTranslation[];
  error?: string;
}

/** POST /api/publisher/translate — RU↔EN renderings of the source. */
export function translate(req: TranslateRequest): Promise<TranslateResponse> {
  return publisherCall<TranslateResponse>(
    '/api/publisher/translate',
    jsonInit({
      source: req.source,
      direction: req.direction,
      count: clamp(req.count, 1, 6),
    }),
  );
}

// ── 1h. generate-title ───────────────────────────────────────────

export interface GenerateTitleResponse {
  title?: string;
  error?: string;
}

/** POST /api/publisher/generate-title — derive a title from a body. 400 on
 *  `body required`. */
export function generateTitle(body: string): Promise<GenerateTitleResponse> {
  return publisherCall<GenerateTitleResponse>(
    '/api/publisher/generate-title',
    jsonInit({ body }),
  );
}

// ── 1c. create (chat variant — skip_ai:true) ─────────────────────

export interface ChatCreateRequest {
  source: SourceContent;
  drafts: Record<Platform, PlatformDraft>;
  enabled_platforms: Platform[];
  selected_channels: SelectedChannel[];
  hermes_creative?: {
    enabled: true;
    route: 'hermes-codex-plugin';
    brief: string;
  };
}

export interface ChatCreateResponse {
  post?: PromotionPost;
  error?: string;
}

/**
 * POST /api/publisher/create (chat variant) — persist a picked suggestion as a
 * post WITHOUT re-running AI (`skip_ai:true`). title+body required → 400.
 */
export function createFromChat(req: ChatCreateRequest): Promise<ChatCreateResponse> {
  return publisherCall<ChatCreateResponse>(
    '/api/publisher/create',
    jsonInit({
      source: req.source,
      drafts: req.drafts,
      skip_ai: true,
      enabled_platforms: req.enabled_platforms,
      selected_channels: req.selected_channels,
      ...(req.hermes_creative ? { hermes_creative: req.hermes_creative } : {}),
    }),
  );
}
